// HTTP transport for the concierge messages endpoint, kept free of React so
// the parsing and retry rules are unit-testable.

import axios from 'axios';
import { REQUEST_CONFIG } from './config';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const postQuery = async (payload, target, token) => {
  const headers = { 'Content-Type': 'application/json' };
  // The concierge messages endpoint authenticates via a `token` header, not
  // `Authorization: Bearer`. The proxy route holds its own credential and
  // must never receive one from the browser.
  if (target.useToken && token) {
    headers.token = token;
  }

  const response = await axios.post(target.url, payload, {
    headers,
    timeout: REQUEST_CONFIG.TIMEOUT
  });

  return response.data;
};

// A response may have been generated even though we never saw it — a timeout
// or cancellation is NOT safe to retry, because resending a non-idempotent
// POST would run a second LLM generation (and open a second conversation when
// no conversation_id is set yet).
const isRetryable = (error) => {
  const status = error.response?.status;
  // A 4xx is a rejected request, not a flaky one — retrying will not help.
  if (status && status >= 400 && status < 500) return false;
  if (status) return true;
  return error.code !== 'ECONNABORTED' && error.code !== 'ERR_CANCELED';
};

export const sendWithRetry = async (payload, target, token) => {
  const { delay } = REQUEST_CONFIG.RETRY;
  // At least one attempt: a 0/negative value (misconfigured
  // REACT_APP_RETRY_ATTEMPTS) would otherwise skip the request entirely and
  // throw undefined.
  const attempts = Math.max(1, REQUEST_CONFIG.RETRY.attempts);
  let lastError;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await postQuery(payload, target, token);
    } catch (error) {
      lastError = error;
      if (!isRetryable(error)) break;
      if (attempt < attempts) {
        await wait(delay * 2 ** (attempt - 1));
      }
    }
  }

  throw lastError;
};

// The concierge messages endpoint returns
//   { conversation: {id, ...}, message: {conversation_id, content, ...} }
// possibly wrapped once more by a proxy as { success, data: {...} }.
const unwrap = (data) =>
  data && typeof data === 'object' && data.data && typeof data.data === 'object'
    ? data.data
    : data;

export const extractAnswer = (data) => {
  if (typeof data === 'string') return data;
  const body = unwrap(data);
  if (!body || typeof body !== 'object') return null;
  // `?? null` (not truthiness) so a legitimately empty answer is preserved.
  // Legacy `answer` kept as a fallback for older proxies.
  return body.message?.content ?? body.answer ?? null;
};

export const extractConversationId = (data) => {
  const body = unwrap(data);
  if (!body || typeof body !== 'object') return null;
  return body.conversation?.id ?? body.message?.conversation_id ?? null;
};

// --- Guest identification ---------------------------------------------------
// Best-effort personalisation: a single attempt with a short timeout, and the
// caller treats any failure as "anonymous guest". The provider answers
//   200 { event: {...}, event_information: [...],
//         guest_information: { guest_id, first_name, last_name, email, ... } }
// for a registered guest, and 403 { status: "error", message:
// "EVENT_API_FORBIDDEN" } for an email not registered for the event.

const IDENTIFY_TIMEOUT_MS = 10000;

export const identifyGuest = async (target, email, key, signal) => {
  const headers = { 'Content-Type': 'application/json' };
  if (key) {
    headers.Authorization = `Bearer ${key}`;
  }

  const response = await axios.post(
    target.url,
    { email },
    { headers, timeout: IDENTIFY_TIMEOUT_MS, signal }
  );

  return response.data;
};

// Returns the guest's first name to greet them by, or null when there is none.
export const extractGuestName = (data) => {
  const name = data?.guest_information?.first_name;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
};
